import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatLines } from 'iconoir-react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useAuth } from '../../contexts/AuthContext';
import { useVoiceInput } from '../../hooks/useVoiceInput';
import {
  getConversations,
  getMessages,
  sendMessage,
  markAsRead,
  uploadAttachment,
  blockUser,
  reportUser,
  deleteConversation,
  type ChatConversation,
  type ChatMessage,
} from '../../services/chatServices';
import { api } from '../../utils';
import { eventService, type EventItem } from '../../services/eventService';
import { InfoTip } from '../common/InfoTip';
import { Toaster } from '../common';
import ReportUserModal from '../common/ReportUserModal';
import toast from 'react-hot-toast';
import { useTrackIceBreakerUsage } from '../../hooks/useMatch';

interface ChatInterfaceProps {
  channelIdFromUrl?: string | null;
  prefillText?: string;
  iceBreaker?: { matchId: string; index: number };
}

/** Conversation List Sidebar */
const ConversationList: React.FC<{
  conversations: ChatConversation[];
  activeId: string | null;
  onSelect: (conv: ChatConversation) => void;
  isLoading: boolean;
  myEvents: EventItem[];
  eventFilter: string;
  onEventFilterChange: (eventId: string) => void;
}> = ({ conversations, activeId, onSelect, isLoading, myEvents, eventFilter, onEventFilterChange }) => {
  if (isLoading) {
    return (
      <div className='w-full border-r border-gray-200 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500' />
      </div>
    );
  }

  return (
    <div className='w-full border-r border-gray-200 flex flex-col'>
      <div className='p-4 border-b border-gray-200'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Inbox <span className='text-sm font-normal text-gray-500'>({conversations.length})</span>
        </h2>
        {/* Multi-event: one shared inbox, filterable by event. Only shown to
            users who actually belong to more than one event. */}
        {myEvents.length > 1 && (
          <select
            value={eventFilter}
            onChange={(e) => onEventFilterChange(e.target.value)}
            className='mt-2 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300'
          >
            <option value=''>All events</option>
            {myEvents.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className='flex-1 overflow-y-auto'>
        {conversations.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full text-center px-6 py-12'>
            <ChatLines className='text-primary-300 mb-3' width={48} height={48} />
            <p className='text-base font-semibold text-gray-700'>
              {eventFilter ? 'No conversations for this event yet' : 'No conversations yet'}
            </p>
            <p className='text-sm text-gray-500 mt-1'>
              When you connect with matches, your conversations will appear here.
            </p>
          </div>
        ) : (
          conversations.map((conv) => {
            const name = conv.otherUser
              ? `${conv.otherUser.firstName} ${conv.otherUser.lastName}`
              : 'Unknown';
            const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
            const preview = conv.lastMessage?.content || 'No messages yet';
            const isActive = conv.id === activeId;
            const isUnread = conv.unreadCount > 0;

            return (
              <div
                key={conv.id}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${isActive ? 'bg-primary-50' : ''}`}
                onClick={() => onSelect(conv)}
              >
                <div className='w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0'>
                  {initials}
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex justify-between items-center'>
                    <span className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                      {name}
                    </span>
                    {conv.lastMessage?.createdAt && (
                      <span className='text-xs text-gray-400 flex-shrink-0 ml-2'>
                        {formatTime(conv.lastMessage.createdAt)}
                      </span>
                    )}
                  </div>
                  {conv.eventName && (
                    <span className='inline-flex items-center rounded bg-gray-100 px-1.5 py-px text-[10px] font-medium text-gray-500 mt-0.5'>
                      {conv.eventName}
                    </span>
                  )}
                  <div className='flex justify-between items-center mt-0.5'>
                    <p className={`text-xs truncate ${isUnread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {preview}
                    </p>
                    {isUnread && (
                      <span className='bg-primary-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ml-2 flex-shrink-0'>
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/** Message Thread */
const MessageThread: React.FC<{
  conversation: ChatConversation | null;
  currentUserId: string;
  onBack?: () => void;
  prefillText?: string;
  iceBreaker?: { matchId: string; index: number };
}> = ({ conversation, currentUserId, onBack, prefillText, iceBreaker }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [modal, setModal] = useState<'block' | 'delete' | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const { isRecording, isTranscribing, toggleRecording } = useVoiceInput((text) => {
    setInputText((prev) => prev ? `${prev} ${text}` : text);
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prefillAppliedRef = useRef(false);
  const iceBreakerTrackedRef = useRef(false);
  const { mutate: trackIceBreaker } = useTrackIceBreakerUsage();

  // Load messages when conversation changes + track chat_opened
  useEffect(() => {
    if (!conversation) return;
    setIsLoading(true);
    // Track chat opened event
    api.post('/analytics/track', {
      event_type: 'ai_chat_start',
      event_category: 'chat',
      event_data: { conversation_id: conversation.id, other_user_id: conversation.otherUser?.id },
    }).catch(() => {});
    getMessages(conversation.id)
      .then((result) => {
        setMessages(result.messages);
        markAsRead(conversation.id).catch(() => {});
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [conversation?.id]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    if (!conversation) return;
    const poll = setInterval(async () => {
      try {
        const result = await getMessages(conversation.id);
        setMessages((prev) => {
          if (result.messages.length !== prev.length) {
            const hasNewFromOther = result.messages.some(
              (m) => !prev.some((p) => p.id === m.id) && m.sender_id !== currentUserId
            );
            if (hasNewFromOther) {
              markAsRead(conversation.id).catch(() => {});
            }
            return result.messages;
          }
          return prev;
        });
      } catch {}
    }, 1500);
    return () => clearInterval(poll);
  }, [conversation?.id, currentUserId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Focus input when conversation changes
  useEffect(() => {
    if (conversation) inputRef.current?.focus();
  }, [conversation?.id]);

  // Pre-fill the composer with a conversation starter chosen on the match card
  // ("Use in chat"). Apply once, only after the target conversation has loaded,
  // so the suggested text actually lands in the input (it previously did not).
  useEffect(() => {
    if (conversation && prefillText && !prefillAppliedRef.current) {
      setInputText(prefillText);
      prefillAppliedRef.current = true;
      inputRef.current?.focus();
    }
  }, [conversation?.id, prefillText]);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || !conversation || isSending) return;
    const text = inputText.trim();
    setInputText('');
    setIsSending(true);

    // Optimistic update
    const optimistic: ChatMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversation.id,
      sender_id: currentUserId,
      content: text,
      message_type: 'text',
      read_at: null,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      const sent = await sendMessage(conversation.id, text);
      setMessages(prev => prev.map(m => m.id === optimistic.id ? sent : m));
      // Track message sent event
      api.post('/analytics/track', {
        event_type: 'message_sent',
        event_category: 'chat',
        event_data: { conversation_id: conversation.id, message_length: text.length },
      }).catch(() => {});
      // Mark the conversation starter "Used" only now that a message has
      // actually been sent — not on click/copy/navigate. Fires once.
      if (iceBreaker && !iceBreakerTrackedRef.current) {
        iceBreakerTrackedRef.current = true;
        trackIceBreaker({ match_id: iceBreaker.matchId, selected_index: iceBreaker.index });
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally {
      setIsSending(false);
    }
  }, [inputText, conversation, currentUserId, isSending, iceBreaker, trackIceBreaker]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !conversation) return;
    setIsUploading(true);
    try {
      const attachment = await uploadAttachment(file, conversation.id);
      const isImage = file.type.startsWith('image/');
      await sendMessage(
        conversation.id,
        isImage ? `📷 ${file.name}` : `📎 ${file.name}`,
        isImage ? 'image' : 'text',
        attachment.url,
        attachment.name,
        attachment.size,
      );
    } catch (err: any) {
      console.error('Upload failed:', err);
      // Jun-19 (a user V1 test): surface the failure instead of swallowing it.
      // Attachments previously failed silently (uploaded to a dead Supabase
      // bucket), so the user saw nothing happen.
      toast.custom(t => (
        <Toaster
          message={
            err?.response?.data?.message ||
            err?.message ||
            'Could not send attachment. Please try a different file.'
          }
          type='error'
          id={t.id}
        />
      ));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [conversation]);

  const handleEmojiSelect = useCallback((emoji: any) => {
    setInputText(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return (
      <div className='flex-1 hidden md:flex flex-col items-center justify-center text-center px-8'>
        <ChatLines className='text-primary-200 mb-4' width={64} height={64} />
        <h3 className='text-xl font-semibold text-gray-700'>Select a conversation</h3>
        <p className='text-sm text-gray-500 mt-1'>
          Choose a conversation from the list to start chatting with your connections.
        </p>
      </div>
    );
  }

  const otherName = conversation.otherUser
    ? `${conversation.otherUser.firstName} ${conversation.otherUser.lastName}`
    : 'Chat';
  const initials = otherName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className='flex-1 flex flex-col'>
      {/* Header */}
      <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200'>
        {/* Back button - mobile only */}
        {onBack && (
          <button onClick={onBack} className='md:hidden p-1 -ml-1 text-gray-600 hover:text-gray-900'>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
        )}
        {/* Jun-19 (a user V1 test): removed the hardcoded green presence dot and
            "Online" label. They were static, so EVERYONE (incl. an offline test
            account like "Dean Ben") always showed as online. The custom chat API
            exposes no presence field, so rather than fake it we show no status
            until real presence is wired. */}
        <div className='w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-semibold'>
          {initials}
        </div>
        <div className='flex-1'>
          <span className='font-semibold text-gray-900 block'>{otherName}</span>
        </div>

        {/* Three-dot menu */}
        <div className='relative'>
          <button onClick={() => setShowMenu(!showMenu)} className='p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100'>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="10" cy="16" r="1.5"/></svg>
          </button>
          {showMenu && (
            <>
              <div className='fixed inset-0 z-40' onClick={() => setShowMenu(false)} />
              <div data-tour-id='chat-menu' className='absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 w-48'>
                <div className='w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-gray-50'>
                  <button onClick={() => { setShowReport(true); setShowMenu(false); }} className='flex-1 text-left text-sm text-gray-700 flex items-center gap-3'>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                    Report
                  </button>
                  <InfoTip tooltipKey='report-user' side='left' />
                </div>
                <div className='w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-red-50'>
                  <button onClick={() => { setModal('block'); setShowMenu(false); }} className='flex-1 text-left text-sm text-red-600 flex items-center gap-3'>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                    Block
                  </button>
                  <InfoTip tooltipKey='block-user' side='left' />
                </div>
                <div className='border-t border-gray-100 my-1' />
                <button onClick={() => { setModal('delete'); setShowMenu(false); }} className='w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3'>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  Delete chat
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modals */}
      {modal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setModal(null)}>
          <div className='bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl' onClick={(e) => e.stopPropagation()}>
            {modal === 'block' && (
              <>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>Block {otherName}?</h3>
                <p className='text-sm text-gray-500 mb-1'>This person won't be able to message you. They won't know you blocked them.</p>
                <label className='flex items-start gap-2 mt-4 mb-6 cursor-pointer'>
                  <input type='checkbox' className='mt-0.5 rounded' onChange={(e) => setReportReason(e.target.checked ? 'Blocked with report' : '')} />
                  <div>
                    <span className='text-sm font-medium text-gray-900'>Report to 2Connect Professional</span>
                    <p className='text-xs text-gray-500'>The last messages will be reviewed by our team.</p>
                  </div>
                </label>
                <div className='flex justify-end gap-3'>
                  <button onClick={() => setModal(null)} className='px-4 py-2 text-sm text-primary-600 hover:bg-gray-50 rounded-lg font-medium'>Cancel</button>
                  <button onClick={async () => {
                    const otherId = conversation?.otherUser?.id;
                    if (!otherId) return;
                    await blockUser(otherId);
                    if (reportReason) { try { await reportUser(otherId, reportReason, '', conversation?.id); } catch { /* report may be a duplicate; the block already applied */ } }
                    setModal(null);
                    setReportReason('');
                    window.location.reload();
                  }} className='px-5 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600'>Block</button>
                </div>
              </>
            )}
            {modal === 'delete' && (
              <>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>Delete chat with {otherName}?</h3>
                <p className='text-sm text-gray-500 mb-6'>Messages will be removed permanently.</p>
                <div className='flex justify-end gap-3'>
                  <button onClick={() => setModal(null)} className='px-4 py-2 text-sm text-primary-600 hover:bg-gray-50 rounded-lg font-medium'>Cancel</button>
                  <button onClick={async () => {
                    if (!conversation) return;
                    await deleteConversation(conversation.id);
                    setModal(null);
                    window.location.reload();
                  }} className='px-5 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600'>Delete</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ReportUserModal
        open={showReport}
        reportedUserId={conversation?.otherUser?.id}
        reportedName={otherName}
        conversationId={conversation?.id}
        connected={true}
        onClose={() => setShowReport(false)}
      />

      {/* Messages */}
      <div className='flex-1 overflow-y-auto px-4 py-4'>
        {isLoading ? (
          <div className='flex items-center justify-center h-full'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500' />
          </div>
        ) : messages.length === 0 ? (
          <div className='flex items-center justify-center h-full text-gray-400 text-sm'>
            No messages yet. Say hello!
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === currentUserId;
            const time = new Date(msg.created_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            });

            if (msg.message_type === 'system') {
              return (
                <div key={msg.id} className='text-center text-xs text-gray-400 italic my-3'>
                  {msg.content}
                </div>
              );
            }

            const isRead = !!msg.read_at;
            const isSent = !msg.id.startsWith('temp-');

            return (
              <div key={msg.id} className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] inline-block px-3 py-2 rounded-lg shadow-sm ${
                    isMe
                      ? 'bg-primary-500 text-white rounded-br-none'
                      : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                  }`}
                >
                  {/* Attachment */}
                  {msg.attachment_url && (
                    msg.message_type === 'image' ? (
                      <a href={msg.attachment_url} target='_blank' rel='noreferrer'>
                        <img src={msg.attachment_url} alt={msg.attachment_name || 'image'} className='max-w-full rounded-md mb-1 max-h-60 object-cover' />
                      </a>
                    ) : (
                      <a href={msg.attachment_url} target='_blank' rel='noreferrer'
                        className={`flex items-center gap-2 px-3 py-2 rounded-md mb-1 ${isMe ? 'bg-white/20' : 'bg-gray-100'}`}>
                        <span className='text-lg'>📄</span>
                        <div className='min-w-0'>
                          <p className={`text-xs font-medium truncate ${isMe ? 'text-white' : 'text-gray-800'}`}>{msg.attachment_name}</p>
                          <p className={`text-[10px] ${isMe ? 'text-white/60' : 'text-gray-400'}`}>{formatFileSize(msg.attachment_size)}</p>
                        </div>
                      </a>
                    )
                  )}
                  {/* Text content (hide if it's just the auto-generated attachment label) */}
                  {(!msg.attachment_url || (!msg.content.startsWith('📷') && !msg.content.startsWith('📎'))) && (
                    <p className={`text-[14px] leading-relaxed ${isMe ? 'text-white' : 'text-gray-800'}`}>
                      {msg.content}
                    </p>
                  )}
                  <div className={`flex items-center justify-end gap-1 mt-1 ${isMe ? 'text-white/70' : 'text-gray-500'}`}>
                    <span className='text-[11px]'>{time}</span>
                    {isMe && (
                      <span className='inline-flex items-center ml-1'>
                        {!isSent ? (
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/><path d="M8 5v3l2 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        ) : isRead ? (
                          <svg width="18" height="12" viewBox="0 0 20 12" fill="none"><path d="M1.5 6.5l3.5 3.5L12 3" stroke="#34B7F1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 6.5l3.5 3.5L18 3" stroke="#34B7F1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        ) : (
                          <svg width="14" height="12" viewBox="0 0 14 12" fill="none"><path d="M1.5 6.5l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/></svg>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className='absolute bottom-20 left-4 z-50'>
          <div className='relative'>
            <div className='absolute inset-0' onClick={() => setShowEmojiPicker(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }} />
            <Picker data={data} onEmojiSelect={handleEmojiSelect} theme='light' previewPosition='none' skinTonePosition='none' />
          </div>
        </div>
      )}

      {/* Input bar */}
      <div className='flex items-center gap-2 px-4 py-3 border-t border-gray-200 relative'>
        {/* Jun-19 (a user V1 test): accept aligned to the backend's allowed MIME
            set (images, PDF, txt/csv/md). .doc/.docx were offered but the backend
            rejects them, which would surface a confusing "unsupported type". */}
        <input ref={fileInputRef} type='file' className='hidden' accept='image/*,.pdf,.txt,.csv,.md' onChange={handleFileUpload} />

        {/* Input field with icons inside */}
        <div className='flex-1 flex items-center bg-gray-50 rounded-full px-3 py-1 focus-within:ring-2 focus-within:ring-primary-200'>
          {/* Emoji icon */}
          <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className='p-1.5 text-gray-400 hover:text-gray-600' title='Emoji'>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
          </button>

          {/* Attach icon */}
          <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className='p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-40' title='Attach file'>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>

          {/* Text input */}
          <input
            ref={inputRef}
            type='text'
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isUploading ? 'Uploading...' : 'Type a message...'}
            className='flex-1 px-2 py-1.5 bg-transparent text-sm outline-none'
            maxLength={2000}
            disabled={isUploading}
          />
        </div>

        {/* Microphone button */}
        <button
          onClick={toggleRecording}
          disabled={isTranscribing}
          className={`p-2.5 rounded-full transition-colors ${
            isRecording
              ? 'bg-red-500 text-white animate-pulse'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          } disabled:opacity-40`}
          title={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Voice input'}
        >
          {isTranscribing ? (
            <div className='w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin' />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          )}
        </button>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!inputText.trim() || isSending}
          className='p-2.5 bg-primary-500 text-white rounded-full disabled:opacity-40 hover:bg-primary-600 transition-colors'
          title='Send'
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
        </button>
      </div>
    </div>
  );
};

/** Main Chat Interface */
export const ChatInterface: React.FC<ChatInterfaceProps> = React.memo(
  ({ channelIdFromUrl, prefillText, iceBreaker }) => {
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    // Multi-event inbox filter ('' = all events). Options load best-effort;
    // the control only renders for users in more than one event.
    const [myEvents, setMyEvents] = useState<EventItem[]>([]);
    const [eventFilter, setEventFilter] = useState('');
    const { user } = useAuth();
    const currentUserId = user?.id || '';

    useEffect(() => {
      eventService
        .myEvents()
        .then((evs) => setMyEvents(Array.isArray(evs) ? evs : []))
        .catch(() => {});
    }, []);

    // Load conversations
    useEffect(() => {
      getConversations()
        .then((convs) => {
          setConversations(convs);
          // Auto-select conversation from URL
          if (channelIdFromUrl) {
            const found = convs.find(c => c.id === channelIdFromUrl);
            if (found) setActiveConversation(found);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }, [channelIdFromUrl]);

    // Refresh conversations periodically
    useEffect(() => {
      const interval = setInterval(() => {
        getConversations().then(setConversations).catch(() => {});
      }, 15000);
      return () => clearInterval(interval);
    }, []);

    return (
      <>
        <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0`}>
          <ConversationList
            conversations={
              eventFilter
                ? conversations.filter((c) => c.eventId === eventFilter)
                : conversations
            }
            activeId={activeConversation?.id || null}
            onSelect={setActiveConversation}
            isLoading={isLoading}
            myEvents={myEvents}
            eventFilter={eventFilter}
            onEventFilterChange={setEventFilter}
          />
        </div>
        <div className={`${!activeConversation ? 'hidden md:flex' : 'flex'} flex-1 min-w-0`}>
          <MessageThread
            conversation={activeConversation}
            currentUserId={currentUserId}
            onBack={() => setActiveConversation(null)}
            prefillText={prefillText}
            iceBreaker={iceBreaker}
          />
        </div>
      </>
    );
  }
);

ChatInterface.displayName = 'ChatInterface';

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// Re-export loading states for backward compatibility
export const LoadingStates = {
  ChatInitializing: () => (
    <div className='w-full h-full flex items-center justify-center'>
      <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500' />
    </div>
  ),
};
